import { createI18n } from 'vue-i18n'

import en from './locales/en'
import { unflattenMessages, type NestedMessages } from './unflatten'

/**
 * Le traduzioni vivono a database e arrivano da `GET /labels/translations/{iso}`
 * (vedi lo store `locales`). Questo modulo è solo il motore di rendering: tiene
 * l'istanza vue-i18n, il locale attivo e il fallback che viaggia nel bundle.
 *
 * L'inglese è l'unica lingua **compilata** nell'app, e serve da rete di
 * sicurezza: se l'API non risponde al primo avvio, l'interfaccia è in inglese
 * invece di mostrare chiavi grezze. ⚠️ `locales/en.ts` è generato — si rigenera
 * dal database con `npm run i18n:pull`, non si modifica a mano.
 */

/** Locale sempre presente nel bundle, e `fallbackLocale` di vue-i18n. */
export const BUNDLED_LOCALE = 'en'

const LOCALE_STORAGE_KEY = 'travick.locale'

/**
 * Forma di un codice lingua, non la sua esistenza.
 *
 * Al primo avvio le lingue non sono ancora arrivate, quindi non c'è nulla contro
 * cui validare: questo controllo evita solo che un valore assurdo finito in
 * localStorage diventi un locale o un pezzo di URL. Che la lingua esista davvero
 * lo verifica lo store una volta caricato l'elenco.
 */
export function isLocaleCode(value: unknown): value is string {
  return typeof value === 'string' && /^[a-z]{2}$/.test(value)
}

/** Scelta fatta da un visitatore non ancora loggato. */
function storedLocale(): string | null {
  try {
    const raw = window.localStorage.getItem(LOCALE_STORAGE_KEY)
    return isLocaleCode(raw) ? raw : null
  } catch {
    return null
  }
}

export function persistLocale(locale: string): void {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch {
    // Non fatale: la scelta non sopravvivrà al reload.
  }
}

/** Default di configurazione, usato quando il visitatore non ha ancora scelto. */
export function configuredLocale(): string {
  return isLocaleCode(import.meta.env.VITE_DEFAULT_LOCALE)
    ? import.meta.env.VITE_DEFAULT_LOCALE
    : BUNDLED_LOCALE
}

/**
 * Locale che il visitatore dovrebbe vedere (per scelta di prodotto non si annusa
 * il browser: il default deve essere deterministico):
 *   1. scelta già fatta in questo browser
 *   2. VITE_DEFAULT_LOCALE
 *   3. l'inglese del bundle
 * A utente loggato vince il suo `language` da getUser (vedi lo store `user`).
 */
export function desiredLocale(): string {
  return storedLocale() ?? configuredLocale()
}

// Schema volutamente lasco: i messaggi arrivano da database, quindi la loro forma
// non è nota al compilatore, e `setLocaleMessage` deve accettare qualunque albero.
const bundledMessages: Record<string, NestedMessages> = { [BUNDLED_LOCALE]: en }

/**
 * Si parte **sempre** dall'inglese del bundle, l'unica lingua di cui i messaggi
 * esistono già: lo store `locales` sposta il locale sulla lingua desiderata appena
 * ne ha i messaggi (dalla cache, quindi subito, o dall'API). Partire direttamente
 * dalla lingua desiderata significherebbe dichiarare `<html lang="it">` su una
 * pagina che sta ancora rendendo l'inglese di fallback.
 */
export const i18n = createI18n({
  legacy: false,
  locale: BUNDLED_LOCALE,
  fallbackLocale: BUNDLED_LOCALE,
  messages: bundledMessages,
  missingWarn: import.meta.env.DEV,
  fallbackWarn: false,
})

export function currentLocale(): string {
  return i18n.global.locale.value
}

/**
 * Rende disponibili a vue-i18n i messaggi arrivati dall'API.
 *
 * Per l'inglese si **fonde** invece di sostituire: una chiave che sta nel bundle
 * ma non (ancora) a database resterebbe altrimenti senza traduzione, e con essa
 * il fallback di tutte le altre lingue.
 */
export function applyMessages(locale: string, flat: Record<string, string>): void {
  const nested = unflattenMessages(flat)

  if (locale === BUNDLED_LOCALE) {
    i18n.global.mergeLocaleMessage(locale, nested)
    return
  }

  i18n.global.setLocaleMessage(locale, nested)
}

/**
 * Applica un locale all'app.
 *
 * ⚠️ Non carica i messaggi: chiamarla per una lingua di cui non sono stati ancora
 * applicati i messaggi mostrerebbe l'app in inglese (il fallback). Il flusso
 * completo è `changeLanguage()` in `services/session.ts`.
 *
 * @param persist tiene la scelta per la prossima visita (da saltare quando il
 *                valore arriva dal server, per non litigare con l'account).
 */
export function setLocale(locale: string, { persist = true }: { persist?: boolean } = {}): void {
  if (!isLocaleCode(locale)) return

  i18n.global.locale.value = locale
  // Tiene allineati screen reader e motori di ricerca a ciò che si vede.
  document.documentElement.setAttribute('lang', locale)

  if (persist) persistLocale(locale)
}

/** Traduce fuori da un componente (guard del router, store, interceptor). */
export function translate(key: string, named?: Record<string, unknown>): string {
  return named ? i18n.global.t(key, named) : i18n.global.t(key)
}
